import React, { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type {
  Event,
  EventCtaMode,
  EventFormOptions,
  EventPublishState,
  EventStatus,
  EventVisibility,
} from '../../types';
import { cn } from '../../lib/utils';
import { toDatetimeLocalValue } from '../../lib/format-datetime';
import { defaultEventFormOptions, PUBLISH_STATE_LABELS } from '../../lib/event-utils';
import {
  clearEditorModule,
  emptyEventModules,
  eventModules,
  MODULE_LABELS,
  moduleUntickDecision,
  persistedModuleFlags,
  statusFromDate,
  type EventModuleKey,
  type EventModules,
} from '../../lib/event-modules';
import { MAX_DROP_POINTS, validateDropPoints } from '../../lib/event-drop-points';
import { parseVenueMapUrl } from '../../lib/venue-map-url';
import {
  fetchEventCardImage,
  fetchEventFlyer,
  type EventMediaChange,
  type EventWritePayload,
} from '../../lib/events-api';
import { prepareCardImageFile, prepareFlyerFile } from '../../lib/event-flyer-compress';
import { isEventCardImagePath, isEventFlyerPath } from '../../lib/event-flyer';
import { MarkdownField } from '../markdown/MarkdownField';
import { useApiJson } from '../../hooks/useApi';
import { hasEventScoresLink, supportsEventScores, withEventScoresLink } from '../../lib/event-scores-link';

const STATUSES: EventStatus[] = ['upcoming', 'active', 'past'];
const PUBLISH_STATES: EventPublishState[] = ['draft', 'published', 'archived'];

export type RegistrationMode = 'open' | 'closed' | 'coming_soon' | 'not_required';

const REGISTRATION_MODES: { value: RegistrationMode; label: string }[] = [
  { value: 'open', label: 'Registrations open' },
  { value: 'closed', label: 'Registrations closed' },
  { value: 'coming_soon', label: 'Coming soon' },
  { value: 'not_required', label: 'Registration not required' },
];

export interface DropPointDraft {
  label: string;
  address: string;
  mapsUrl: string;
}

export interface EventEditorState {
  title: string;
  description: string;
  date: string;
  location: string;
  venueMapUrl: string;
  image: string;
  cardImage: string;
  status: EventStatus;
  ctaMode: EventCtaMode;
  ctaLabel: string;
  ctaUrl: string;
  coordinator: string;
  specialMention: string;
  participantCountOverride: string;
  detailsUrl: string;
  detailsLabel: string;
  galleryAlbumId: string;
  slug: string;
  regPrefix: string;
  publishState: EventPublishState;
  visibility: EventVisibility;
  registrationMode: RegistrationMode;
  deadlineAt: string;
  capacity: string;
  feeMember: string;
  feeNonMember: string;
  upiId: string;
  upiQrUrl: string;
  formOptions: EventFormOptions;
  teamEnabled: boolean;
  teamMin: string;
  teamMax: string;
  notesEnabled: boolean;
  dropPoints: DropPointDraft[];
  pickupEnabled: boolean;
  whatsappReminderEnabled: boolean;
  modules: EventModules;
  /** When false, status follows the date. When true, `status` is kept as stored. */
  timingOverride: boolean;
}

export function emptyEditorState(): EventEditorState {
  return {
    title: '',
    description: '',
    date: '',
    location: '',
    venueMapUrl: '',
    image: '',
    cardImage: '',
    status: 'upcoming',
    ctaMode: 'none',
    ctaLabel: '',
    ctaUrl: '',
    coordinator: '',
    specialMention: '',
    participantCountOverride: '',
    detailsUrl: '',
    detailsLabel: '',
    galleryAlbumId: '',
    slug: '',
    regPrefix: '',
    publishState: 'draft',
    visibility: 'public',
    registrationMode: 'open',
    deadlineAt: '',
    capacity: '',
    feeMember: '',
    feeNonMember: '',
    upiId: '',
    upiQrUrl: '',
    formOptions: defaultEventFormOptions(),
    teamEnabled: false,
    teamMin: '2',
    teamMax: '4',
    notesEnabled: false,
    dropPoints: [],
    pickupEnabled: false,
    whatsappReminderEnabled: false,
    modules: emptyEventModules(true),
    timingOverride: false,
  };
}

const numText = (n: number | null | undefined) => (n == null ? '' : String(n));

export function editorStateFromEvent(event: Event): EventEditorState {
  const opts = event.formOptions ?? defaultEventFormOptions();
  const derivedStatus = statusFromDate(event.date);
  return {
    title: event.title,
    description: event.description ?? '',
    date: toDatetimeLocalValue(event.date),
    location: event.location ?? '',
    venueMapUrl: event.venueMapUrl ?? '',
    image: event.image ?? '',
    cardImage: event.cardImage ?? '',
    status: event.status,
    ctaMode: event.ctaMode,
    ctaLabel: event.ctaLabel ?? '',
    ctaUrl: event.ctaUrl ?? '',
    coordinator: event.coordinator ?? '',
    specialMention: event.specialMention ?? '',
    participantCountOverride: numText(event.participantCountOverride),
    detailsUrl: event.detailsUrl ?? '',
    detailsLabel: event.detailsLabel ?? '',
    galleryAlbumId: event.galleryAlbumId ?? '',
    slug: event.slug ?? '',
    regPrefix: event.regPrefix ?? '',
    publishState: event.publishState,
    visibility: event.visibility,
    registrationMode: event.registrationNotRequired
      ? 'not_required'
      : event.registrationComingSoon || event.registrationClosedReason === 'coming_soon'
        ? 'coming_soon'
        : event.registrationToggle
          ? 'open'
          : 'closed',
    deadlineAt: toDatetimeLocalValue(event.deadlineAt ?? ''),
    capacity: numText(event.capacity),
    feeMember: numText(event.feeMember),
    feeNonMember: numText(event.feeNonMember),
    upiId: event.upiId ?? '',
    upiQrUrl: event.upiQrUrl ?? '',
    formOptions: opts,
    teamEnabled: opts.teamSize != null,
    teamMin: String(opts.teamSize?.min ?? 2),
    teamMax: String(opts.teamSize?.max ?? 4),
    notesEnabled: opts.notesQuestion != null,
    dropPoints: (event.dropPoints ?? []).map((point) => ({
      label: point.label ?? '',
      address: point.address,
      mapsUrl: point.mapsUrl ?? '',
    })),
    pickupEnabled: event.pickupEnabled === true,
    whatsappReminderEnabled: event.whatsappReminderEnabled === true,
    modules: eventModules(event),
    timingOverride: event.status !== derivedStatus,
  };
}

function optionalInt(value: string, label: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < 0) throw new Error(`${label} must be a whole number`);
  return n;
}

const nullIfBlank = (value: string) => value.trim() || null;

export function payloadFromEditorState(form: EventEditorState, _isCreate: boolean): EventWritePayload {
  const leavingDraft = form.publishState !== 'draft';
  if (!form.title.trim()) {
    throw new Error(leavingDraft ? 'Add a title before this event can leave draft.' : 'Title is required');
  }
  if (!form.date.trim()) {
    throw new Error(
      leavingDraft ? 'Add a date and time before this event can leave draft.' : 'Date and time is required',
    );
  }
  const date = new Date(form.date);
  if (Number.isNaN(date.getTime())) throw new Error('Enter a valid date and time');

  const opts = form.formOptions;
  const teamMin = Number(form.teamMin);
  const teamMax = Number(form.teamMax);
  if (form.teamEnabled && (!Number.isInteger(teamMin) || !Number.isInteger(teamMax) || teamMin < 1 || teamMax < teamMin || teamMax < 2)) {
    throw new Error('Team size needs a minimum of at least 1 and a maximum of at least 2');
  }
  if (form.notesEnabled && !form.formOptions.notesQuestion?.trim()) {
    throw new Error('Enter the question to ask, or turn off the free-text question');
  }

  const optsWithoutModules: EventFormOptions = { ...opts };
  delete optsWithoutModules.modules;
  const registrationOn = form.registrationMode !== 'not_required';
  const formOptions: EventFormOptions = {
    ...optsWithoutModules,
    teamSize: form.teamEnabled ? { min: teamMin, max: teamMax } : null,
    notesQuestion: form.notesEnabled ? opts.notesQuestion?.trim() || null : null,
    notesRequired: form.notesEnabled && opts.notesRequired,
    minAgeYears: opts.collectDob ? opts.minAgeYears : null,
    ageAsOn: opts.collectDob && opts.minAgeYears != null ? opts.ageAsOn : null,
    paymentInstructions: opts.paymentRequired ? opts.paymentInstructions?.trim() || null : null,
    whatsappGroupUrl: opts.whatsappGroupUrl?.trim() || null,
    whatsappGroupPublic: opts.whatsappGroupPublic === true,
  };
  const remembered = persistedModuleFlags({ ...form, modules: { ...form.modules, registration: registrationOn } });
  if (remembered) formOptions.modules = remembered;

  const venueMap = parseVenueMapUrl(form.venueMapUrl);
  if (venueMap.ok === false) throw new Error(venueMap.error);

  const payload: EventWritePayload = {
    title: form.title.trim(),
    description: form.description.trim(),
    date: date.toISOString(),
    location: form.location.trim(),
    venueMapUrl: venueMap.url,
    status: form.timingOverride ? form.status : statusFromDate(form.date),
    ctaMode: form.ctaMode,
    ctaLabel: form.ctaMode === 'none' ? null : nullIfBlank(form.ctaLabel),
    ctaUrl: form.ctaMode === 'none' ? null : nullIfBlank(form.ctaUrl),
    coordinator: nullIfBlank(form.coordinator),
    specialMention: nullIfBlank(form.specialMention),
    participantCountOverride: optionalInt(form.participantCountOverride, 'Participant count override'),
    detailsUrl: nullIfBlank(form.detailsUrl),
    detailsLabel: nullIfBlank(form.detailsLabel),
    galleryAlbumId: nullIfBlank(form.galleryAlbumId),
    publishState: form.publishState,
    visibility: form.visibility,
    registrationOpen: form.registrationMode === 'open',
    registrationNotRequired: form.registrationMode === 'not_required',
    registrationComingSoon: form.registrationMode === 'coming_soon',
    deadlineAt: form.deadlineAt ? new Date(form.deadlineAt).toISOString() : null,
    capacity: optionalInt(form.capacity, 'Capacity'),
    feeMember: opts.memberChoice ? optionalInt(form.feeMember, 'Member fee') : null,
    feeNonMember: optionalInt(form.feeNonMember, 'Fee'),
    upiId: opts.paymentRequired ? nullIfBlank(form.upiId) : null,
    upiQrUrl: opts.paymentRequired ? nullIfBlank(form.upiQrUrl) : null,
    formOptions,
  };
  if (form.slug.trim()) payload.slug = form.slug.trim().toLowerCase();
  if (form.regPrefix.trim()) payload.regPrefix = form.regPrefix.trim().toUpperCase();

  const dropPoints = validateDropPoints(
    form.dropPoints.map((point) => ({
      label: point.label,
      address: point.address,
      mapsUrl: point.mapsUrl.trim() || null,
    })),
  );
  if (dropPoints.ok === false) throw new Error(dropPoints.error);
  payload.dropPoints = dropPoints.points;
  payload.pickupEnabled = form.pickupEnabled;
  payload.whatsappReminderEnabled = form.whatsappReminderEnabled;
  return payload;
}

const inputClass =
  'w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-2.5 px-3.5 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent-subtle)] transition-all duration-[var(--duration-fast)] text-sm disabled:opacity-50';

function Field({
  label,
  hint,
  className,
  children,
}: {
  label: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={cn('block', className)}>
      <span className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-xs text-[var(--color-text-tertiary)] mt-1">{hint}</span>}
    </label>
  );
}

function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(ev) => onChange(ev.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-[var(--color-border)] accent-[var(--color-accent)]"
      />
      <span>
        <span className="block text-sm font-medium text-[var(--color-text-primary)]">{label}</span>
        {description && (
          <span className="block text-xs text-[var(--color-text-tertiary)] mt-0.5">{description}</span>
        )}
      </span>
    </label>
  );
}

function registrationModeDescription(mode: RegistrationMode, publishState: EventPublishState): string {
  if (publishState === 'archived') {
    return 'Archived events stay in the past list.';
  }
  if (mode === 'not_required') {
    return publishState === 'published'
      ? 'Published. The public site and the app show No registration needed. The Join WhatsApp group button still appears when that link is public.'
      : 'Saved for when you publish. The public site and the app will show No registration needed. The Join WhatsApp group button still appears when that link is public.';
  }
  if (mode === 'coming_soon') {
    return publishState === 'published'
      ? 'Published. Event is visible, but registrations open later.'
      : 'Saved for when you publish. Event is visible, but registrations open later.';
  }
  if (publishState !== 'published') {
    return mode === 'open'
      ? 'Saved as open. Publish the event and the public site will show Registrations open.'
      : 'Saved as closed. Publish the event and the public site will show Registrations closed until you choose another option.';
  }
  return mode === 'open'
    ? 'Published. The public site shows Registrations open until the deadline passes or the event is full.'
    : 'Published. The public site shows Registrations closed.';
}

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduce(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);
  return reduce;
}

/** Opens on the next frame and stays mounted until the close transition finishes. */
function useExpand(open: boolean): { entered: boolean; present: boolean } {
  const reduce = usePrefersReducedMotion();
  const [entered, setEntered] = useState(open);
  const [present, setPresent] = useState(open);
  useEffect(() => {
    if (reduce) {
      setEntered(open);
      setPresent(open);
      return;
    }
    if (open) {
      setPresent(true);
      const frame = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(frame);
    }
    setEntered(false);
    const timer = window.setTimeout(() => setPresent(false), 200);
    return () => window.clearTimeout(timer);
  }, [open, reduce]);
  return { entered, present };
}

function ModuleRow({
  moduleKey,
  checked,
  locked,
  hint,
  folded,
  onToggle,
  onFold,
  description,
  children,
}: {
  moduleKey: EventModuleKey;
  checked: boolean;
  locked: boolean;
  hint?: string;
  folded: boolean;
  onToggle: (key: EventModuleKey, next: boolean) => void;
  onFold: (key: EventModuleKey) => void;
  description?: string;
  children: React.ReactNode;
}) {
  const label = MODULE_LABELS[moduleKey];
  const open = checked && !folded;
  const { entered, present } = useExpand(open);
  // The tint is the card's own background. A child fill clipped to this radius
  // covers the 1px border on the curve at phone pixel densities.
  return (
    <div
      className={cn(
        'rounded-[var(--radius-md)] border',
        checked ? 'border-[var(--color-accent)] bg-[var(--color-accent-subtle)]' : 'border-[var(--color-border)]',
        locked && 'opacity-50',
      )}
    >
      <div className="flex items-center gap-1 pr-1">
        <label className={cn('flex min-h-11 flex-1 items-start gap-2.5 px-3 py-2.5', locked ? '' : 'cursor-pointer')}>
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-accent)]"
            checked={checked}
            disabled={locked}
            onChange={(ev) => onToggle(moduleKey, ev.target.checked)}
          />
          <span>
            <span className="block text-sm font-medium text-[var(--color-text-primary)]">{label}</span>
            {hint && <span className="block text-xs text-[var(--color-text-tertiary)] mt-0.5">{hint}</span>}
          </span>
        </label>
        {checked && (
          <button
            type="button"
            aria-expanded={open}
            aria-label={`${open ? 'Hide' : 'Show'} ${label} fields`}
            onClick={() => onFold(moduleKey)}
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-2)]"
          >
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform duration-200 ease-out motion-reduce:transition-none',
                open && 'rotate-180',
              )}
              aria-hidden="true"
            />
          </button>
        )}
      </div>
      {present && (
        <div
          className={cn(
            'grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none',
            entered ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
        >
          <div className="min-h-0 overflow-hidden" inert={entered ? undefined : true} aria-hidden={entered ? undefined : true}>
            <div className="space-y-4 rounded-b-[calc(var(--radius-md)-1px)] border-t border-[var(--color-border)] bg-[var(--color-surface-1)] px-3 py-3">
              {description && <p className="text-xs text-[var(--color-text-secondary)]">{description}</p>}
              {children}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <section className="pt-5 border-t border-[var(--color-border)] first:pt-0 first:border-t-0 space-y-4">
      <div>
        <h4 className="text-sm font-semibold text-[var(--color-text-primary)]">{title}</h4>
        {description && <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">{description}</p>}
      </div>
      {children}
    </section>
  );
}

export function EventEditorForm({
  initial,
  isCreate,
  eventId,
  submitLabel,
  rosterCount = 0,
  roster,
  onSubmit,
  onCancel,
}: {
  initial: EventEditorState;
  isCreate: boolean;
  /** Set while editing so a stored flyer can be previewed, including drafts. */
  eventId?: string;
  submitLabel: string;
  /** People already on the roster. Turning the section off is blocked while this is above zero. */
  rosterCount?: number;
  /** Shown inside the roster section while editing an existing event. */
  roster?: React.ReactNode;
  onSubmit: (payload: EventWritePayload, media: EventMediaChange) => Promise<void>;
  onCancel: () => void;
}) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flyerFile, setFlyerFile] = useState<File | null>(null);
  const [flyerRemoved, setFlyerRemoved] = useState(false);
  const [flyerError, setFlyerError] = useState<string | null>(null);
  const [preparingFlyer, setPreparingFlyer] = useState(false);
  const [pickedUrl, setPickedUrl] = useState<string | null>(null);
  const [storedFlyerUrl, setStoredFlyerUrl] = useState<string | null>(null);
  const [storedFlyerFailed, setStoredFlyerFailed] = useState(false);
  const [cardFile, setCardFile] = useState<File | null>(null);
  const [cardRemoved, setCardRemoved] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [preparingCard, setPreparingCard] = useState(false);
  const [cardPickedUrl, setCardPickedUrl] = useState<string | null>(null);
  const [storedCardUrl, setStoredCardUrl] = useState<string | null>(null);
  const [storedCardFailed, setStoredCardFailed] = useState(false);
  /** Ticked sections the admin folded shut. The checkbox stays on, so the data stays. */
  const [folded, setFolded] = useState<Partial<Record<EventModuleKey, boolean>>>({});
  const { data: galleryData } = useApiJson<{ albums: { id: string; title: string; date: string }[] }>('/api/gallery/albums');

  const set = <K extends keyof EventEditorState>(key: K, value: EventEditorState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleModule = (key: EventModuleKey, next: boolean) => {
    if (next) {
      if ((key === 'fees' || key === 'questions') && !form.modules.registration) return;
      setError(null);
      setFolded((prev) => ({ ...prev, [key]: false }));
      setForm((prev) => ({
        ...prev,
        modules: { ...prev.modules, [key]: true },
        registrationMode:
          key === 'registration' && prev.registrationMode === 'not_required' ? 'open' : prev.registrationMode,
      }));
      return;
    }
    const decision = moduleUntickDecision(form, key, rosterCount);
    if (decision.action === 'block') {
      setError(decision.message);
      return;
    }
    if (decision.action === 'confirm' && !window.confirm(decision.message)) return;
    setError(null);
    setForm((prev) => clearEditorModule(prev, key));
  };

  const derivedStatus = statusFromDate(form.date);
  const setOpt = <K extends keyof EventFormOptions>(key: K, value: EventFormOptions[K]) =>
    setForm((prev) => ({ ...prev, formOptions: { ...prev.formOptions, [key]: value } }));
  const opts = form.formOptions;

  const updateDropPoint = (index: number, patch: Partial<DropPointDraft>) =>
    setForm((prev) => ({
      ...prev,
      dropPoints: prev.dropPoints.map((point, i) => (i === index ? { ...point, ...patch } : point)),
    }));
  const moveDropPoint = (index: number, direction: -1 | 1) =>
    setForm((prev) => {
      const next = [...prev.dropPoints];
      const target = index + direction;
      const current = next[index];
      const swap = next[target];
      if (!current || !swap) return prev;
      next[index] = swap;
      next[target] = current;
      return { ...prev, dropPoints: next };
    });
  const removeDropPoint = (index: number) =>
    setForm((prev) => ({ ...prev, dropPoints: prev.dropPoints.filter((_, i) => i !== index) }));
  const addDropPoint = () =>
    setForm((prev) =>
      prev.dropPoints.length >= MAX_DROP_POINTS
        ? prev
        : { ...prev, dropPoints: [...prev.dropPoints, { label: '', address: '', mapsUrl: '' }] },
    );

  useEffect(() => {
    if (!flyerFile) {
      setPickedUrl(null);
      return;
    }
    const url = URL.createObjectURL(flyerFile);
    setPickedUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [flyerFile]);

  useEffect(() => {
    if (!cardFile) {
      setCardPickedUrl(null);
      return;
    }
    const url = URL.createObjectURL(cardFile);
    setCardPickedUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [cardFile]);

  useEffect(() => {
    if (flyerFile || flyerRemoved || !eventId || !isEventFlyerPath(form.image)) {
      setStoredFlyerUrl(null);
      setStoredFlyerFailed(false);
      return;
    }
    let cancelled = false;
    let url: string | null = null;
    setStoredFlyerFailed(false);
    fetchEventFlyer(eventId)
      .then((blob) => {
        url = URL.createObjectURL(blob);
        if (cancelled) URL.revokeObjectURL(url);
        else setStoredFlyerUrl(url);
      })
      .catch(() => {
        if (!cancelled) setStoredFlyerFailed(true);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [eventId, flyerFile, flyerRemoved, form.image]);

  useEffect(() => {
    if (cardFile || cardRemoved || !eventId || !isEventCardImagePath(form.cardImage)) {
      setStoredCardUrl(null);
      setStoredCardFailed(false);
      return;
    }
    let cancelled = false;
    let url: string | null = null;
    setStoredCardFailed(false);
    fetchEventCardImage(eventId)
      .then((blob) => {
        url = URL.createObjectURL(blob);
        if (cancelled) URL.revokeObjectURL(url);
        else setStoredCardUrl(url);
      })
      .catch(() => {
        if (!cancelled) setStoredCardFailed(true);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [eventId, cardFile, cardRemoved, form.cardImage]);

  const previewSrc = flyerFile
    ? pickedUrl
    : flyerRemoved
      ? null
      : isEventFlyerPath(form.image)
        ? storedFlyerUrl
        : form.image || null;

  const cardPreviewSrc = cardFile
    ? cardPickedUrl
    : cardRemoved
      ? null
      : isEventCardImagePath(form.cardImage)
        ? storedCardUrl
        : form.cardImage || null;

  const onPickFlyer = async (picked: File) => {
    setPreparingFlyer(true);
    setFlyerError(null);
    try {
      const ready = await prepareFlyerFile(picked);
      setFlyerFile(ready);
      setFlyerRemoved(false);
    } catch (err) {
      setFlyerError(err instanceof Error ? err.message : 'Could not use that image.');
    } finally {
      setPreparingFlyer(false);
    }
  };

  const removeFlyer = () => {
    setFlyerError(null);
    if (flyerFile) {
      setFlyerFile(null);
      return;
    }
    setFlyerRemoved(true);
  };

  const onPickCard = async (picked: File) => {
    setPreparingCard(true);
    setCardError(null);
    try {
      const ready = await prepareCardImageFile(picked);
      setCardFile(ready);
      setCardRemoved(false);
    } catch (err) {
      setCardError(err instanceof Error ? err.message : 'Could not use that image.');
    } finally {
      setPreparingCard(false);
    }
  };

  const removeCard = () => {
    setCardError(null);
    if (cardFile) {
      setCardFile(null);
      return;
    }
    setCardRemoved(true);
  };

  const canRemove = Boolean(flyerFile || previewSrc || (form.image && !flyerRemoved));
  const canRemoveCard = Boolean(cardFile || cardPreviewSrc || (form.cardImage && !cardRemoved));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (preparingFlyer || preparingCard) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit(payloadFromEditorState(form, isCreate), {
        flyer: {
          file: flyerFile,
          remove: flyerRemoved && !flyerFile && Boolean(form.image),
        },
        card: {
          file: cardFile,
          remove: cardRemoved && !cardFile && Boolean(form.cardImage),
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="bg-[var(--color-surface-1)] p-6 rounded-[var(--radius-lg)] border border-[var(--color-border)] space-y-5"
    >
      <Section title="Event details">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Title" hint="Required.">
            <input
              type="text"
              name="title"
              className={inputClass}
              value={form.title}
              onChange={(ev) => set('title', ev.target.value)}
              required
            />
          </Field>
          <Field label="Date & time" hint="Required. Events are listed and sorted by this.">
            <input
              type="datetime-local"
              name="date"
              className={inputClass}
              value={form.date}
              onChange={(ev) => set('date', ev.target.value)}
              required
            />
          </Field>
          <Field label="Location" hint="Optional.">
            <input
              type="text"
              name="location"
              className={inputClass}
              value={form.location}
              onChange={(ev) => set('location', ev.target.value)}
            />
          </Field>
          <Field
            label="Venue map link"
            hint="Optional. An http or https link, such as a Google Maps link."
          >
            <input
              type="url"
              name="venueMapUrl"
              inputMode="url"
              placeholder="https://maps.app.goo.gl/…"
              className={inputClass}
              value={form.venueMapUrl}
              onChange={(ev) => set('venueMapUrl', ev.target.value)}
            />
          </Field>
          <div className="md:col-span-2">
            <Toggle
              checked={form.whatsappReminderEnabled}
              onChange={(value) => set('whatsappReminderEnabled', value)}
              label="Send WhatsApp reminder the day before"
              description="At 6 PM IST the day before, message everyone registered except cancelled payments."
            />
          </div>
          <MarkdownField
            label="Description"
            hint="Optional. Markdown supported: bold, italic, headings, lists, and links. Line breaks are kept."
            name="description"
            className="md:col-span-2"
            rows={6}
            value={form.description}
            onChange={(value) => set('description', value)}
          />
          <div className="md:col-span-2 space-y-2">
            <span className="block text-xs font-medium text-[var(--color-text-secondary)]">Full flyer</span>
            {previewSrc ? (
              <img
                src={previewSrc}
                alt="Event flyer preview"
                className="max-h-72 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-2)] object-contain"
              />
            ) : (
              <div className="flex h-40 items-center justify-center rounded-[var(--radius-md)] border border-dashed border-[var(--color-border)] bg-[var(--color-surface-2)] text-sm text-[var(--color-text-tertiary)]">
                {isEventFlyerPath(form.image) && !flyerRemoved && !flyerFile && !storedFlyerFailed
                  ? 'Loading flyer…'
                  : 'No flyer yet'}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <label
                className={cn(
                  'cursor-pointer px-3 py-2 border border-[var(--color-border)] rounded-[var(--radius-md)] text-[var(--color-text-secondary)] font-medium text-sm hover:bg-[var(--color-surface-2)] transition-colors',
                  preparingFlyer && 'opacity-50 pointer-events-none',
                )}
              >
                {preparingFlyer ? 'Preparing…' : previewSrc ? 'Replace flyer' : 'Choose flyer'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                  className="sr-only"
                  disabled={preparingFlyer}
                  onChange={(ev) => {
                    const picked = ev.target.files?.[0];
                    ev.target.value = '';
                    if (picked) void onPickFlyer(picked);
                  }}
                />
              </label>
              {canRemove && (
                <button
                  type="button"
                  onClick={removeFlyer}
                  className="px-3 py-2 border border-[var(--color-border)] rounded-[var(--radius-md)] text-[var(--color-text-secondary)] font-medium text-sm hover:bg-[var(--color-surface-2)] transition-colors"
                >
                  Remove
                </button>
              )}
            </div>
            <p className="text-xs text-[var(--color-text-tertiary)]">
              Any size, e.g. Instagram 4:5 (1080×1350). JPEG, PNG, or WebP. Large images are compressed to about 1 MB.
            </p>
            {flyerError && (
              <p
                role="alert"
                className="text-sm text-[var(--color-error-text)] bg-[var(--color-error-subtle)] rounded-[var(--radius-md)] px-3 py-2"
              >
                {flyerError}
              </p>
            )}
            {storedFlyerFailed && !flyerFile && !flyerRemoved && (
              <p className="text-xs text-[var(--color-text-tertiary)]">Could not load the saved flyer preview.</p>
            )}
          </div>
          <div className="md:col-span-2 space-y-2">
            <span className="block text-xs font-medium text-[var(--color-text-secondary)]">Card image</span>
            {cardPreviewSrc ? (
              <img
                src={cardPreviewSrc}
                alt="Card image preview"
                className="aspect-video w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-2)] object-cover"
              />
            ) : (
              <div className="flex aspect-video w-full items-center justify-center rounded-[var(--radius-md)] border border-dashed border-[var(--color-border)] bg-[var(--color-surface-2)] text-sm text-[var(--color-text-tertiary)]">
                {isEventCardImagePath(form.cardImage) && !cardRemoved && !cardFile && !storedCardFailed
                  ? 'Loading card image…'
                  : 'No card image yet'}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <label
                className={cn(
                  'cursor-pointer px-3 py-2 border border-[var(--color-border)] rounded-[var(--radius-md)] text-[var(--color-text-secondary)] font-medium text-sm hover:bg-[var(--color-surface-2)] transition-colors',
                  preparingCard && 'opacity-50 pointer-events-none',
                )}
              >
                {preparingCard ? 'Preparing…' : cardPreviewSrc ? 'Replace image' : 'Choose image'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                  className="sr-only"
                  disabled={preparingCard}
                  onChange={(ev) => {
                    const picked = ev.target.files?.[0];
                    ev.target.value = '';
                    if (picked) void onPickCard(picked);
                  }}
                />
              </label>
              {canRemoveCard && (
                <button
                  type="button"
                  onClick={removeCard}
                  className="px-3 py-2 border border-[var(--color-border)] rounded-[var(--radius-md)] text-[var(--color-text-secondary)] font-medium text-sm hover:bg-[var(--color-surface-2)] transition-colors"
                >
                  Remove
                </button>
              )}
            </div>
            <p className="text-xs text-[var(--color-text-tertiary)]">
              Landscape 16:9, 1200×675. JPEG, PNG, or WebP. Large images are compressed to about 400 KB.
            </p>
            {cardError && (
              <p
                role="alert"
                className="text-sm text-[var(--color-error-text)] bg-[var(--color-error-subtle)] rounded-[var(--radius-md)] px-3 py-2"
              >
                {cardError}
              </p>
            )}
            {storedCardFailed && !cardFile && !cardRemoved && (
              <p className="text-xs text-[var(--color-text-tertiary)]">Could not load the saved card image preview.</p>
            )}
          </div>
        </div>
      </Section>

      <Section
        title="Publishing"
        description="Drafts are hidden everywhere. Archived events stay visible as past events. Publishing needs a title and a date; location, description, fees, a flyer, and deadlines can be added later."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="State">
            <select
              className={inputClass}
              value={form.publishState}
              onChange={(ev) => set('publishState', ev.target.value as EventPublishState)}
            >
              {PUBLISH_STATES.map((s) => (
                <option key={s} value={s}>
                  {PUBLISH_STATE_LABELS[s]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Who can see it">
            <select
              className={inputClass}
              value={form.visibility}
              onChange={(ev) => set('visibility', ev.target.value as EventVisibility)}
            >
              <option value="public">Everyone (public site and app)</option>
              <option value="members">Signed-in members only (app)</option>
            </select>
          </Field>
        </div>
      </Section>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Sections</h3>
        <p className="text-xs text-[var(--color-text-secondary)]">
          Tick a section to edit it. Turning one off asks before any saved details are removed.
        </p>
      <ModuleRow
        moduleKey="registration"
        checked={form.modules.registration}
        locked={false}
        hint={form.modules.registration ? undefined : 'No registration needed.'}
        folded={folded.registration === true}
        onToggle={toggleModule}
        onFold={(key) => setFolded((prev) => ({ ...prev, [key]: !prev[key] }))}
        description="Open, closed, or coming soon, plus an optional deadline and capacity."
      >
        <fieldset className="space-y-2">
          <legend className="sr-only">Registration</legend>
          {REGISTRATION_MODES.filter((mode) => mode.value !== 'not_required').map((mode) => (
            <label key={mode.value} className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="registrationMode"
                value={mode.value}
                checked={form.registrationMode === mode.value}
                onChange={() => set('registrationMode', mode.value)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-accent)]"
              />
              <span className="text-sm font-medium text-[var(--color-text-primary)]">{mode.label}</span>
            </label>
          ))}
          <p className="text-xs text-[var(--color-text-tertiary)]">
            {registrationModeDescription(form.registrationMode, form.publishState)}
          </p>
        </fieldset>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Deadline" hint="Optional. Registrations close automatically after this.">
            <input
              type="datetime-local"
              className={inputClass}
              value={form.deadlineAt}
              onChange={(ev) => set('deadlineAt', ev.target.value)}
            />
          </Field>
          <Field label="Capacity" hint="Optional. Counts registrations (one per team), excluding cancelled ones.">
            <input
              type="number"
              min={1}
              className={inputClass}
              value={form.capacity}
              onChange={(ev) => set('capacity', ev.target.value)}
            />
          </Field>
        </div>
      </ModuleRow>

      <ModuleRow
        moduleKey="fees"
        checked={form.modules.fees}
        locked={!form.modules.registration && !form.modules.fees}
        hint={!form.modules.registration && !form.modules.fees ? 'Turn on Registration first.' : undefined}
        folded={folded.fees === true}
        onToggle={toggleModule}
        onFold={(key) => setFolded((prev) => ({ ...prev, [key]: !prev[key] }))}
        description="Payment is offline. Registrants upload a screenshot."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Toggle
            checked={opts.paymentRequired}
            onChange={(v) => setOpt('paymentRequired', v)}
            label="Payment required"
            description="Registrants must upload a payment screenshot."
          />
          <Toggle
            checked={opts.memberChoice}
            onChange={(v) => setOpt('memberChoice', v)}
            label="Separate member fee"
            description="Registrants choose member or non-member."
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {opts.memberChoice && (
            <Field label="Member fee (₹)">
              <input
                type="number"
                min={0}
                className={inputClass}
                value={form.feeMember}
                onChange={(ev) => set('feeMember', ev.target.value)}
              />
            </Field>
          )}
          <Field label={opts.memberChoice ? 'Non-member fee (₹)' : 'Fee (₹)'} hint="Leave blank for free events.">
            <input
              type="number"
              min={0}
              className={inputClass}
              value={form.feeNonMember}
              onChange={(ev) => set('feeNonMember', ev.target.value)}
            />
          </Field>
        </div>
        {opts.paymentRequired && (
          <MarkdownField
            label="Extra payment note"
            name="paymentNote"
            hint="Optional. The SMYM bank account, copy buttons, and WhatsApp helpline are shown automatically. Markdown supported."
            rows={4}
            value={opts.paymentInstructions ?? ''}
            onChange={(value) => setOpt('paymentInstructions', value)}
          />
        )}
      </ModuleRow>

      <ModuleRow
        moduleKey="questions"
        checked={form.modules.questions}
        locked={!form.modules.registration && !form.modules.questions}
        hint={!form.modules.registration && !form.modules.questions ? 'Turn on Registration first.' : undefined}
        folded={folded.questions === true}
        onToggle={toggleModule}
        onFold={(key) => setFolded((prev) => ({ ...prev, [key]: !prev[key] }))}
        description="Name, phone and email are always asked. Add only what this event needs."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Toggle checked={opts.collectDob} onChange={(v) => setOpt('collectDob', v)} label="Date of birth" />
          <Toggle checked={opts.collectGender} onChange={(v) => setOpt('collectGender', v)} label="Gender" />
        </div>
        {opts.collectDob && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-7">
            <Field label="Minimum age" hint="Optional.">
              <input
                type="number"
                min={1}
                className={inputClass}
                value={opts.minAgeYears ?? ''}
                onChange={(ev) => setOpt('minAgeYears', ev.target.value === '' ? null : Number(ev.target.value))}
              />
            </Field>
            <Field label="Age measured on" hint="Defaults to the event date.">
              <input
                type="date"
                className={inputClass}
                value={opts.ageAsOn ?? ''}
                disabled={opts.minAgeYears == null}
                onChange={(ev) => setOpt('ageAsOn', ev.target.value || null)}
              />
            </Field>
          </div>
        )}
        <Toggle
          checked={form.teamEnabled}
          onChange={(v) => set('teamEnabled', v)}
          label="Team registration"
          description="The registrant adds teammate names."
        />
        {form.teamEnabled && (
          <div className="grid grid-cols-2 gap-4 pl-7">
            <Field label="Minimum team size" hint="Including the registrant.">
              <input
                type="number"
                min={1}
                className={inputClass}
                value={form.teamMin}
                onChange={(ev) => set('teamMin', ev.target.value)}
              />
            </Field>
            <Field label="Maximum team size">
              <input
                type="number"
                min={2}
                max={20}
                className={inputClass}
                value={form.teamMax}
                onChange={(ev) => set('teamMax', ev.target.value)}
              />
            </Field>
          </div>
        )}
        <Toggle
          checked={form.notesEnabled}
          onChange={(v) => set('notesEnabled', v)}
          label="Free-text question"
        />
        {form.notesEnabled && (
          <div className="space-y-3 pl-7">
            <Field label="Question">
              <input
                type="text"
                placeholder="e.g. Any dietary preferences?"
                className={inputClass}
                value={opts.notesQuestion ?? ''}
                onChange={(ev) => setOpt('notesQuestion', ev.target.value)}
              />
            </Field>
            <Toggle checked={opts.notesRequired} onChange={(v) => setOpt('notesRequired', v)} label="Answer required" />
          </div>
        )}
      </ModuleRow>

      <ModuleRow
        moduleKey="dropPoints"
        checked={form.modules.dropPoints}
        locked={false}
        folded={folded.dropPoints === true}
        onToggle={toggleModule}
        onFold={(key) => setFolded((prev) => ({ ...prev, [key]: !prev[key] }))}
        description="Optional places people can leave items. Shown on the event card and in details. Works with any registration mode."
      >
        <div className="space-y-4">
          {form.dropPoints.map((point, index) => (
            <div
              key={index}
              className="rounded-[var(--radius-md)] border border-[var(--color-border)] p-3 space-y-3"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-[var(--color-text-secondary)]">Drop point {index + 1}</p>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="min-h-9 px-2 text-xs font-medium text-[var(--color-text-secondary)] disabled:opacity-40"
                    disabled={index === 0}
                    onClick={() => moveDropPoint(index, -1)}
                  >
                    Up
                  </button>
                  <button
                    type="button"
                    className="min-h-9 px-2 text-xs font-medium text-[var(--color-text-secondary)] disabled:opacity-40"
                    disabled={index === form.dropPoints.length - 1}
                    onClick={() => moveDropPoint(index, 1)}
                  >
                    Down
                  </button>
                  <button
                    type="button"
                    className="min-h-9 px-2 text-xs font-medium text-[var(--color-error-text)]"
                    onClick={() => removeDropPoint(index)}
                  >
                    Remove
                  </button>
                </div>
              </div>
              <Field label="Name or label" hint="Optional.">
                <input
                  type="text"
                  name={`dropPointLabel-${index}`}
                  placeholder="Anna Nagar"
                  className={inputClass}
                  maxLength={80}
                  value={point.label}
                  onChange={(ev) => updateDropPoint(index, { label: ev.target.value })}
                />
              </Field>
              <Field label="Location address">
                <input
                  type="text"
                  name={`dropPointAddress-${index}`}
                  placeholder="Street, area, landmark"
                  className={inputClass}
                  required
                  maxLength={300}
                  value={point.address}
                  onChange={(ev) => updateDropPoint(index, { address: ev.target.value })}
                />
              </Field>
              <Field label="Google Maps link" hint="Optional. https://maps.app.goo.gl/… or https://www.google.com/maps/…">
                <input
                  type="url"
                  name={`dropPointMaps-${index}`}
                  placeholder="https://maps.app.goo.gl/…"
                  className={inputClass}
                  value={point.mapsUrl}
                  onChange={(ev) => updateDropPoint(index, { mapsUrl: ev.target.value })}
                />
              </Field>
            </div>
          ))}
          <button
            type="button"
            className="inline-flex items-center justify-center min-h-11 px-3 rounded-[var(--radius-md)] text-sm font-medium border border-[var(--color-border)] text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)] disabled:opacity-50"
            disabled={form.dropPoints.length >= MAX_DROP_POINTS}
            onClick={addDropPoint}
          >
            Add drop point
          </button>
        </div>
        <Toggle
          checked={form.pickupEnabled}
          onChange={(value) => set('pickupEnabled', value)}
          label="Request pickup"
          description="Adds a Request pickup button. People send their name, phone, and address without signing in."
        />
      </ModuleRow>

      <ModuleRow
        moduleKey="whatsapp"
        checked={form.modules.whatsapp}
        locked={false}
        folded={folded.whatsapp === true}
        onToggle={toggleModule}
        onFold={(key) => setFolded((prev) => ({ ...prev, [key]: !prev[key] }))}
        description={
          form.registrationMode === 'not_required'
            ? 'Show the group link publicly to put Join WhatsApp group on the event card and in details.'
            : 'The invite stays on the registration confirmation unless you show it publicly.'
        }
      >
        <Field label="Group link" hint="Optional. https://chat.whatsapp.com/…">
          <input
            type="url"
            placeholder="https://chat.whatsapp.com/…"
            className={inputClass}
            value={opts.whatsappGroupUrl ?? ''}
            onChange={(ev) => setOpt('whatsappGroupUrl', ev.target.value)}
          />
        </Field>
        <Toggle
          checked={opts.whatsappGroupPublic}
          onChange={(v) => setOpt('whatsappGroupPublic', v)}
          label="Show WhatsApp group link publicly"
          description={
            opts.whatsappGroupPublic
              ? 'A Join WhatsApp group button appears on the event card and in details. Anyone with the page can open it.'
              : 'Shown with the registration number after someone registers.'
          }
        />
      </ModuleRow>

      <ModuleRow
        moduleKey="cta"
        checked={form.modules.cta}
        locked={false}
        folded={folded.cta === true}
        onToggle={toggleModule}
        onFold={(key) => setFolded((prev) => ({ ...prev, [key]: !prev[key] }))}
        description="Optional button on the members app event card."
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <select
            className={inputClass}
            value={form.ctaMode}
            onChange={(ev) => set('ctaMode', ev.target.value as EventCtaMode)}
            aria-label="CTA mode"
          >
            <option value="none">No button</option>
            <option value="internal">Inside the app</option>
            <option value="external">External link</option>
          </select>
          <input
            type="text"
            placeholder="Button label"
            className={inputClass}
            value={form.ctaLabel}
            onChange={(ev) => set('ctaLabel', ev.target.value)}
            disabled={form.ctaMode === 'none'}
            aria-label="CTA label"
          />
          <input
            type="text"
            placeholder={form.ctaMode === 'internal' ? '/leaderboard' : 'https://…'}
            className={inputClass}
            value={form.ctaUrl}
            onChange={(ev) => set('ctaUrl', ev.target.value)}
            disabled={form.ctaMode === 'none'}
            aria-label="CTA URL"
          />
        </div>
      </ModuleRow>

      {supportsEventScores(form) ? (
        <Section title="Event scores">
          <Toggle
            checked={hasEventScoresLink(form)}
            onChange={(visible) => setForm((prev) => withEventScoresLink(prev, visible))}
            label="Show View scores"
            description="Show the score button on this event's past-event card. Save Changes to apply. Hiding the button keeps all scores saved."
          />
        </Section>
      ) : null}

      <ModuleRow
        moduleKey="highlights"
        checked={form.modules.highlights}
        locked={false}
        folded={folded.highlights === true}
        onToggle={toggleModule}
        onFold={(key) => setFolded((prev) => ({ ...prev, [key]: !prev[key] }))}
        description="Shown when the event is in the past."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Coordinator">
            <input
              type="text"
              className={inputClass}
              value={form.coordinator}
              onChange={(ev) => set('coordinator', ev.target.value)}
            />
          </Field>
          <Field label="Participant count override" hint="Leave blank to count registrations.">
            <input
              type="number"
              min={0}
              className={inputClass}
              value={form.participantCountOverride}
              onChange={(ev) => set('participantCountOverride', ev.target.value)}
            />
          </Field>
          <MarkdownField
            label="Special mention"
            name="specialMention"
            className="md:col-span-2"
            rows={3}
            value={form.specialMention}
            onChange={(value) => set('specialMention', value)}
            hint="Optional. Markdown supported."
          />
          <Field label="Details link" hint="Optional. A path such as /bulandi-2026 or an https link.">
            <input
              type="text"
              className={inputClass}
              value={form.detailsUrl}
              onChange={(ev) => set('detailsUrl', ev.target.value)}
              placeholder="/bulandi-2026"
            />
          </Field>
          <Field label="Details label" hint="Button text, for example Explore Bulandi 2026.">
            <input
              type="text"
              className={inputClass}
              value={form.detailsLabel}
              onChange={(ev) => set('detailsLabel', ev.target.value)}
              placeholder="Explore Bulandi 2026"
            />
          </Field>
          <Field label="Album" hint="Optional gallery album for this event." className="md:col-span-2">
            <select
              className={inputClass}
              value={form.galleryAlbumId}
              onChange={(ev) => set('galleryAlbumId', ev.target.value)}
              aria-label="Album"
            >
              <option value="">No album</option>
              {(galleryData?.albums ?? []).map((album) => (
                <option key={album.id} value={album.id}>
                  {album.title} ({album.date})
                </option>
              ))}
            </select>
          </Field>
        </div>
      </ModuleRow>

      <ModuleRow
        moduleKey="roster"
        checked={form.modules.roster}
        locked={false}
        folded={folded.roster === true}
        onToggle={toggleModule}
        onFold={(key) => setFolded((prev) => ({ ...prev, [key]: !prev[key] }))}
        description="People listed on the members app, separate from public registrations."
      >
        {roster ?? (
          <p className="text-sm text-[var(--color-text-secondary)]">Save the event, then you can add people to the roster.</p>
        )}
      </ModuleRow>
      </div>

      <details className="border-t border-[var(--color-border)] pt-4">
        <summary className="cursor-pointer text-sm font-semibold text-[var(--color-text-primary)]">Advanced</summary>
        <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
          Slug, registration prefix, and a timing override. Timing follows the date unless you change it. UPI ID and QR are unused on the public form.
        </p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field
            label="URL slug"
            hint={
              isCreate
                ? 'Optional. Leave blank to generate from the title.'
                : 'Optional. Leave blank to keep the current slug. Used by the public site.'
            }
          >
            <input
              type="text"
              name="slug"
              placeholder="rifle-shooting-2026"
              className={inputClass}
              value={form.slug}
              onChange={(ev) => set('slug', ev.target.value)}
            />
          </Field>
          <Field
            label="Registration number prefix"
            hint={
              isCreate
                ? 'Optional. 2–6 letters or numbers, e.g. RFL. Generated when left blank.'
                : 'Optional. 2–6 letters or numbers. Leave blank to keep the current prefix.'
            }
          >
            <input
              type="text"
              name="regPrefix"
              placeholder="RFL"
              className={cn(inputClass, 'uppercase')}
              value={form.regPrefix}
              onChange={(ev) => set('regPrefix', ev.target.value)}
              maxLength={6}
            />
          </Field>
          <Field
            label="Timing"
            hint={
              form.timingOverride
                ? 'This overrides the date.'
                : `Follows the date: ${derivedStatus}.`
            }
          >
            <select
              className={inputClass}
              aria-label="Timing"
              value={form.timingOverride ? form.status : 'auto'}
              onChange={(ev) => {
                const value = ev.target.value;
                if (value === 'auto') {
                  setForm((prev) => ({ ...prev, timingOverride: false, status: statusFromDate(prev.date) }));
                  return;
                }
                setForm((prev) => ({ ...prev, timingOverride: true, status: value as EventStatus }));
              }}
            >
              <option value="auto">Follows the date</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s === 'active' ? 'Happening now' : s === 'past' ? 'Past' : 'Upcoming'}
                </option>
              ))}
            </select>
          </Field>
          <Field label="UPI ID" hint="Unused. Saved only when payment is required.">
            <input
              type="text"
              placeholder="name@bank"
              className={inputClass}
              value={form.upiId}
              onChange={(ev) => set('upiId', ev.target.value)}
            />
          </Field>
          <Field label="UPI QR image URL" hint="Unused. Saved only when payment is required." className="md:col-span-2">
            <input
              type="text"
              placeholder="https://…"
              className={inputClass}
              value={form.upiQrUrl}
              onChange={(ev) => set('upiQrUrl', ev.target.value)}
            />
          </Field>
        </div>
      </details>

      {error && (
        <p
          role="alert"
          className="text-sm text-[var(--color-error-text)] bg-[var(--color-error-subtle)] rounded-[var(--radius-md)] px-3 py-2"
        >
          {error}
        </p>
      )}

      <div className="sticky bottom-16 z-30 -mx-6 flex gap-3 border-t border-[var(--color-border)] bg-[var(--color-surface-1)] px-6 py-3 md:bottom-0">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 min-h-11 py-3 border border-[var(--color-border)] rounded-[var(--radius-md)] text-[var(--color-text-secondary)] font-medium text-sm hover:bg-[var(--color-surface-2)] transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || preparingFlyer}
          className="flex-1 min-h-11 py-3 bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white font-medium rounded-[var(--radius-md)] transition-colors text-sm disabled:opacity-50"
        >
          {saving ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
